import { createFileRoute } from "@tanstack/react-router";
import { AppShell } from "@/components/layout/app-shell";
import { ProtectedRoute } from "@/components/layout/protected-route";
import {
  Card,
  CardContent,
  CardDescription,
  CardHeader,
  CardTitle,
} from "@/components/ui/card";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { IconSearch, IconRefresh } from "@tabler/icons-react";
import { useState } from "react";
import { useAuditLogs } from "@/hooks/use-admin";
import type { AuditLogParams } from "@/types/api";

export const Route = createFileRoute("/audit-logs/")({
  component: AuditLogsPage,
});

function AuditLogsPage() {
  return (
    <ProtectedRoute allowedRoles={["admin", "super_admin"]}>
      <AppShell>
        <AuditLogsContent />
      </AppShell>
    </ProtectedRoute>
  );
}

function AuditLogsContent() {
  const [page, setPage] = useState(1);
  const [searchQuery, setSearchQuery] = useState("");
  const [actionFilter, setActionFilter] = useState("all");
  const [resourceTypeFilter, setResourceTypeFilter] = useState("all");

  const params: AuditLogParams = {
    page,
    limit: 50,
    action: actionFilter !== "all" ? actionFilter : undefined,
    resourceType:
      resourceTypeFilter !== "all" ? resourceTypeFilter : undefined,
  };

  const { data, isLoading, error, refetch } = useAuditLogs(params);

  const getActionColor = (action: string) => {
    if (action.includes("CREATE") || action.includes("APPROVE"))
      return "default";
    if (action.includes("UPDATE") || action.includes("REGENERATE"))
      return "secondary";
    if (action.includes("DELETE") || action.includes("REJECT"))
      return "destructive";
    return "outline";
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Audit Logs</h1>
          <p className="text-muted-foreground">
            Track all admin actions and system events
          </p>
        </div>
        <Button variant="outline" size="sm" onClick={() => refetch()}>
          <IconRefresh className="mr-2 h-4 w-4" />
          Refresh
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>Activity Log</CardTitle>
          <CardDescription>Complete audit trail of all actions</CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Filters */}
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <div className="relative">
              <IconSearch className="absolute left-3 top-2.5 h-4 w-4 text-muted-foreground" />
              <Input
                placeholder="Search by username..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-9"
              />
            </div>
            <Select value={actionFilter} onValueChange={setActionFilter}>
              <SelectTrigger>
                <SelectValue placeholder="All Actions" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Actions</SelectItem>
                <SelectItem value="ORDER_APPROVE">Order Approve</SelectItem>
                <SelectItem value="ORDER_REJECT">Order Reject</SelectItem>
                <SelectItem value="MERCHANT_CREATE">Merchant Create</SelectItem>
                <SelectItem value="MERCHANT_UPDATE">Merchant Update</SelectItem>
                <SelectItem value="MERCHANT_SUSPEND">
                  Merchant Suspend
                </SelectItem>
                <SelectItem value="IP_BLACKLIST">IP Blacklist</SelectItem>
                <SelectItem value="KILL_SWITCH">Kill Switch Toggle</SelectItem>
              </SelectContent>
            </Select>
            <Select
              value={resourceTypeFilter}
              onValueChange={setResourceTypeFilter}
            >
              <SelectTrigger>
                <SelectValue placeholder="All Resources" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="all">All Resources</SelectItem>
                <SelectItem value="order">Orders</SelectItem>
                <SelectItem value="merchant">Merchants</SelectItem>
                <SelectItem value="security">Security</SelectItem>
                <SelectItem value="admin">Admin</SelectItem>
              </SelectContent>
            </Select>
          </div>

          {/* Table */}
          {isLoading ? (
            <div className="flex items-center justify-center h-64">
              <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64 text-sm text-destructive">
              Failed to load audit logs
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Timestamp</TableHead>
                  <TableHead>User</TableHead>
                  <TableHead>Action</TableHead>
                  <TableHead>Resource</TableHead>
                  <TableHead>IP Address</TableHead>
                  <TableHead>Details</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {data?.data && data.data.length > 0 ? (
                  data.data
                    .filter((log) =>
                      searchQuery
                        ? log.username
                            .toLowerCase()
                            .includes(searchQuery.toLowerCase())
                        : true,
                    )
                    .map((log) => (
                      <TableRow key={log.id}>
                        <TableCell>
                          <div className="text-sm">
                            {new Date(log.createdAt).toLocaleString()}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="font-medium">{log.username}</div>
                          <div className="text-xs text-muted-foreground">
                            {log.ipAddress}
                          </div>
                        </TableCell>
                        <TableCell>
                          <Badge variant={getActionColor(log.action)}>
                            {log.action}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm">{log.resourceType}</div>
                          {log.resourceId && (
                            <code className="text-xs text-muted-foreground">
                              {log.resourceId.substring(0, 8)}...
                            </code>
                          )}
                        </TableCell>
                        <TableCell>
                          <code className="text-xs">{log.ipAddress}</code>
                        </TableCell>
                        <TableCell>
                          <div className="text-sm max-w-md truncate">
                            {JSON.stringify(log.details)}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))
                ) : (
                  <TableRow>
                    <TableCell colSpan={6} className="h-24 text-center">
                      No audit logs found
                    </TableCell>
                  </TableRow>
                )}
              </TableBody>
            </Table>
          )}

          {/* Pagination */}
          {data?.pagination && (
            <div className="flex items-center justify-between">
              <p className="text-sm text-muted-foreground">
                Showing page {data.pagination.page} of{" "}
                {data.pagination.totalPages}
              </p>
              <div className="flex gap-2">
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page - 1)}
                  disabled={page === 1}
                >
                  Previous
                </Button>
                <Button
                  variant="outline"
                  size="sm"
                  onClick={() => setPage(page + 1)}
                  disabled={page >= data.pagination.totalPages}
                >
                  Next
                </Button>
              </div>
            </div>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
